import type { LandingDomain } from '../types/landingCatalog.types'
import {
  LANDING_COMMUNITY_CATALOG_FEATURES,
  LANDING_WRITE_CATALOG_FEATURES,
} from './landingCatalogFeatures'

export const LANDING_CATALOG_PATH = '/funcionalidades'

/**
 * Public landing domains. Append an entry to surface it on the hero rotation,
 * the selector, the phrase controls and the catalog. A future "read"
 * domain only needs its copy here; tones are styled by `tone`.
 */
export const LANDING_DOMAINS: LandingDomain[] = [
  {
    id: 'write',
    anchor: 'escritura',
    title: 'Escritura',
    tabsLabel: 'Etapas de escritura',
    kicker: 'A solas con lo que quieres contar',
    exploreTitle: 'Para escribirla',
    explorationText:
      'Un espacio para planificar, organizar y escribir, con herramientas que te acompañan en cada etapa de la historia.',
    heroWord: 'escrita',
    heroDescription:
      'Planifica tramas, personajes y mundos, y escribe cada día con metas y modo concentración. Todo en un mismo proyecto, gratis y en español.',
    heroIcon: 'mdi-feather',
    heroLinkLabel: 'Ver herramientas para escribir ↓',
    trackState: 'escribir',
    selectorShort: 'Planifica, organiza y escribe tu obra.',
    selectorChips: ['Tramas', 'Fichas', 'Mapas', 'Editor', 'Metas'],
    selectorNote: 'Sin registro: empiezas a escribir en segundos.',
    selectorAction: 'Ver herramientas disponibles →',
    tone: 'warm',
    catalogFeatures: LANDING_WRITE_CATALOG_FEATURES,
    groups: [
      {
        id: 'plan',
        label: 'Planifica',
        features: [
          {
            id: 'plot',
            icon: 'mdi-sitemap',
            title: 'Organizador de tramas',
            description:
              'Estructura la historia y desarrolla sus arcos sin perder de vista el conjunto.',
          },
          {
            id: 'whiteboard',
            icon: 'mdi-draw',
            title: 'Pizarra',
            description: 'Ordena ideas sueltas y marca las conexiones entre ellas.',
          },
          {
            id: 'maps',
            icon: 'mdi-map',
            title: 'Mapas',
            description: 'Sube tu mapa y define las localizaciones de tu historia.',
          },
        ],
      },
      {
        id: 'organize',
        label: 'Organiza',
        features: [
          {
            id: 'sheets',
            icon: 'mdi-card-account-details',
            title: 'Fichas',
            description: 'Guarda lo que sabes de personajes, lugares y objetos en un solo lugar.',
          },
          {
            id: 'notes',
            icon: 'mdi-note-text',
            title: 'Notas',
            description: 'Anota ideas y observaciones antes de que se te escapen.',
          },
          {
            id: 'tasks',
            icon: 'mdi-checkbox-marked-outline',
            title: 'Pendientes',
            description: 'Lleva las tareas del proyecto junto a la historia.',
          },
        ],
      },
      {
        id: 'write',
        label: 'Escribe',
        features: [
          {
            id: 'editor',
            icon: 'mdi-text-box-edit',
            title: 'Editor',
            description: 'Escribe la obra en el espacio pensado para quedarte en la página.',
          },
          {
            id: 'focus',
            icon: 'mdi-eye-off-outline',
            title: 'Concentración',
            description: 'Reduce lo que distrae y quédate con el texto.',
          },
          {
            id: 'goals',
            icon: 'mdi-star',
            title: 'Metas diarias',
            description: 'Mantente motivado a escribir cada día.',
          },
        ],
      },
    ],
  },
  {
    id: 'community',
    anchor: 'comunidad',
    title: 'Comunidad',
    tabsLabel: 'Formas de participar',
    kicker: 'Con quienes entienden esa pasión',
    exploreTitle: 'Para compartirla',
    explorationText:
      'Un espacio para mostrarte como escritor, compartir lo que creas y encontrar a otras personas que también escriben.',
    heroWord: 'compartida',
    heroDescription:
      'Comparte tu obra con lectores beta, únete a grupos y sprints de escritura, y encuentra escritores que entienden tu pasión.',
    heroIcon: 'mdi-account-group-outline',
    heroLinkLabel: 'Conocer la comunidad ↓',
    trackState: 'compartir',
    selectorShort: 'Comparte y conecta con escritores.',
    selectorChips: ['Perfil', 'Grupos', 'Chats', 'Sprints', 'Lectores beta'],
    selectorNote: 'Compartes solo lo que eliges: tu manuscrito sigue privado.',
    selectorAction: 'Ver la comunidad →',
    tone: 'cool',
    catalogFeatures: LANDING_COMMUNITY_CATALOG_FEATURES,
    groups: [
      {
        id: 'express',
        label: 'Exprésate',
        features: [
          {
            id: 'profile',
            icon: 'mdi-account-circle-outline',
            title: 'Perfil público',
            description: 'Preséntate, arma tu identidad y muestra tu actividad literaria.',
          },
          {
            id: 'capsule',
            icon: 'mdi-cube-outline',
            title: 'Cápsula',
            description: 'Publica piezas de lo que estás creando y de tu vida como escritor.',
          },
        ],
      },
      {
        id: 'share',
        label: 'Comparte',
        features: [
          {
            id: 'snapshots',
            icon: 'mdi-share-variant-outline',
            title: 'Snapshots compartibles',
            description: 'Comparte una instantánea de tu contenido con quien quieras.',
          },
          {
            id: 'comments',
            icon: 'mdi-comment-text-outline',
            title: 'Comentarios',
            description: 'Quienes leen pueden dejar un comentario en la historia.',
          },
        ],
      },
      {
        id: 'connect',
        label: 'Conecta',
        features: [
          {
            id: 'groups',
            icon: 'mdi-account-multiple-outline',
            title: 'Grupos',
            description: 'Participa en espacios para conversar con otros escritores.',
          },
          {
            id: 'chats',
            icon: 'mdi-chat-outline',
            title: 'Chats',
            description: 'Conversa con otros miembros cuando quieras seguir la charla.',
          },
          {
            id: 'activity',
            icon: 'mdi-newspaper-variant-outline',
            title: 'Actividad',
            description: 'Sigue a otros escritores y felicítalos cuando avanzan.',
          },
        ],
      },
    ],
  },
]

export function landingCatalogTo(anchor?: string) {
  if (!anchor) {
    return LANDING_CATALOG_PATH
  }
  return { path: LANDING_CATALOG_PATH, hash: `#${anchor}` }
}
